import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Search, Compass, MapPin } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const STATES = ['NSW', 'VIC', 'QLD', 'SA', 'WA', 'TAS', 'NT', 'ACT'];

export default function AdminPilotAvailability() {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editLocation, setEditLocation] = useState(null); // { listing, suburb, state }
  const adminEmail = typeof window !== 'undefined' ? (localStorage.getItem('userEmail') || 'info@trucknsendit.com') : 'info@trucknsendit.com';

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ admin_email: adminEmail });
      if (search.trim()) params.set('search', search.trim());
      const res = await axios.get(`${API}/admin/pilots/availability?${params.toString()}`);
      setListings(res.data || []);
    } catch (e) {
      console.error(e);
      alert(e.response?.data?.detail || 'Failed to load pilot listings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleSaveLocation = async () => {
    if (!editLocation) return;
    const s = (editLocation.suburb || '').trim();
    const st = (editLocation.state || '').trim();
    if (!s || !st) { alert('Suburb and state are required'); return; }
    try {
      await axios.patch(`${API}/admin/pilots/availability/${editLocation.listing.id}/location?admin_email=${encodeURIComponent(adminEmail)}`, {
        location_suburb: s,
        location_state: st,
      });
      setEditLocation(null);
      load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to update location');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />
      <main className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 py-6">
        <div className="mb-4">
          <h1 className="text-2xl font-bold text-[#1e3a5f] flex items-center gap-2"><Compass className="w-6 h-6" /> Pilot Availability — Admin</h1>
          <p className="text-sm text-gray-500">Search by name, pilot code, email, suburb or listing ID. Click &quot;Edit location&quot; to move a pilot on the map (auto-geocoded).</p>
        </div>

        <Card className="p-4 mb-4">
          <div className="grid sm:grid-cols-[1fr_auto] gap-3 items-end">
            <div>
              <Label>Search by name, pilot code, email, suburb, or listing ID</Label>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && load()}
                  className="pl-9"
                  placeholder="e.g. John Smith, PL001, john@…, Sydney"
                  data-testid="admin-pilot-search-input"
                />
              </div>
            </div>
            <Button onClick={load} className="bg-[#1e3a5f]" data-testid="admin-pilot-search-btn">Search</Button>
          </div>
        </Card>

        {loading ? (
          <p className="text-center py-10 text-gray-500">Loading…</p>
        ) : listings.length === 0 ? (
          <p className="text-center py-10 text-gray-500">No pilot listings found.</p>
        ) : (
          <div className="space-y-2">
            {listings.map(l => (
              <Card
                key={l.id}
                className="p-3 border-l-4 border-l-blue-500"
                data-testid={`admin-pilot-row-${l.id}`}
              >
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      {l.pilot_code && (
                        <span
                          className="text-[11px] font-bold px-2 py-0.5 rounded bg-[#1e3a5f] text-white font-mono"
                          data-testid={`admin-pilot-code-${l.id}`}
                        >
                          {l.pilot_code}
                        </span>
                      )}
                      <span className="font-semibold text-[#1e3a5f]">{l.pilot_name || 'Unnamed pilot'}</span>
                      <span className="text-xs text-gray-500">— {l.pilot_email}</span>
                    </div>
                    <div className="text-xs text-gray-600 mt-1 flex items-center gap-1">
                      <MapPin className="w-3 h-3" /> {l.location_suburb || '—'}, {l.location_state || '—'}
                      {typeof l.location_lat === 'number' && typeof l.location_lng === 'number' && (
                        <span className="text-gray-400 ml-2 font-mono">({l.location_lat.toFixed(4)}, {l.location_lng.toFixed(4)})</span>
                      )}
                    </div>
                  </div>
                  <div className="flex-shrink-0">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setEditLocation({ listing: l, suburb: l.location_suburb || '', state: l.location_state || 'NSW' })}
                      className="text-slate-700 border-slate-400 hover:bg-slate-50"
                      data-testid={`admin-pilot-edit-location-${l.id}`}
                    >
                      <MapPin className="w-3.5 h-3.5 mr-1" /> Edit location
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        {editLocation && (
          <div
            className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center p-4"
            onClick={() => setEditLocation(null)}
            data-testid="admin-pilot-location-backdrop"
          >
            <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl" onClick={(e) => e.stopPropagation()}>
              <h3 className="text-lg font-bold text-[#1e3a5f] mb-1 flex items-center gap-2"><MapPin className="w-5 h-5" /> Update pilot location</h3>
              <p className="text-sm text-gray-600 mb-3">
                <strong>{editLocation.listing.pilot_code || editLocation.listing.id.slice(0, 8)}</strong> — {editLocation.listing.pilot_name || editLocation.listing.pilot_email}
              </p>
              <div className="grid grid-cols-[2fr_1fr] gap-3">
                <div>
                  <Label>Suburb *</Label>
                  <Input
                    value={editLocation.suburb}
                    onChange={(e) => setEditLocation({ ...editLocation, suburb: e.target.value })}
                    placeholder="e.g. Newcastle"
                    data-testid="admin-pilot-location-suburb"
                  />
                </div>
                <div>
                  <Label>State *</Label>
                  <select
                    value={editLocation.state}
                    onChange={(e) => setEditLocation({ ...editLocation, state: e.target.value })}
                    className="w-full h-10 border rounded-md px-3 text-sm"
                    data-testid="admin-pilot-location-state"
                  >
                    {STATES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2">Latitude/longitude will be auto-geocoded from suburb + state.</p>
              <div className="flex gap-2 mt-4">
                <Button variant="outline" onClick={() => setEditLocation(null)} className="flex-1" data-testid="admin-pilot-location-cancel">Cancel</Button>
                <Button onClick={handleSaveLocation} className="flex-1 bg-[#1e3a5f] hover:bg-[#15304f] text-white" data-testid="admin-pilot-location-save">Save location</Button>
              </div>
            </div>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
